import React, { Fragment, useState } from 'react';
import { css } from 'twin.macro';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { VscLoading } from 'react-icons/vsc';
import { Button, Modal, Table, Spinner, Dropdown } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { connect } from 'react-redux';
import { cleanUpString, fetchWrapper, isValidEmail, isValidPhone } from '../../../../_helpers';
import FileUploader from '../../Common/FileUploader';
import { showBulkReport } from '../Client/BulkModal';
import { HiUsers } from 'react-icons/hi2';
import { BootstrapToolTip } from '../../../../_elements/ToolTip';
import { FaInfoCircle, FaEdit, FaSave, FaTimes } from 'react-icons/fa';

const preparePayload = (items) => {
    return (items || [])
        .map((item) => {
            const { name: nameObj, phone: phoneObj, email: emailObj, role: roleObj } = item;
            const name = nameObj?.label;
            const phone = phoneObj?.label || undefined;
            const email = emailObj?.label || undefined;
            const role = roleObj?.label;
            if (!name || (!isValidPhone(phone) && !isValidEmail(email))) return null;
            return {
                name,
                phone,
                email,
                role,
            };
        })
        .filter(Boolean);
};

const getRole = ({ teamId, roomId, role = '' }) => {
    try {
        let selectedRole = 'user';

        if (!role) {
            return selectedRole;
        }
        const validRoles = roomId ? ['supervisor', 'doctor', 'family', 'user'] : ['user', 'teamManager'];
        const matchedRole = validRoles.find((validRole) => {
            const regex = new RegExp(`^${validRole}$`, 'i');
            return regex.test(role);
        });
        if (matchedRole) {
            selectedRole = matchedRole;
        }
        return selectedRole;
    } catch (error) {
        console.log('error', error);
        return 'user';
    }
};

const formatPhoneWithCountryCode = (phone = '', countryCode = '') => {
    if (!phone) return '';
    if (phone.includes('+')) return phone;
    if (!countryCode) return phone;
    const cleanPhone = phone.replace(/[^\d]/g, '');
    return `${countryCode}${cleanPhone}`;
};

const validatePhone = (phone) => {
    if (!phone) return 'Phone is missing';
    if (!phone.includes('+')) return 'Error missing country code or +';
    if (!isValidPhone(phone)) return 'Invalid phone number';
    return '';
};

const BulkModal = ({ callback = async () => {}, bulkCategory = 'User', roomId, teamId }) => {
    const [show, setShow] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [items, setItems] = useState([]);
    const [errors, setErrors] = useState([]);
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [countryCodePrefix, setCountryCodePrefix] = useState('');

    const handleFileUpload = async (formData) => {
        try {
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                if (jsonData.length > 0) {
                    const tempItems = jsonData
                        .slice(1)
                        .map((row) => {
                            const cleanedRow = row.map(cleanUpString);
                            const [name, phone, email, role] = cleanedRow;

                            return !phone && !name
                                ? null
                                : {
                                      name: { label: name, error: !name && 'Name is required' },
                                      phone: {
                                          label: phone || '',
                                          error: validatePhone(phone),
                                      },
                                      email: {
                                          label: email,
                                          error: !email ? 'Email is missing' : !isValidEmail(email) && 'Invalid email',
                                      },
                                      role: {
                                          label: getRole({ teamId, roomId, role }),
                                      },
                                  };
                        })
                        .filter(Boolean);

                    setItems(tempItems);
                    setErrors([]);
                } else {
                    setErrors(['No data found in the file']);
                }
                setFileData(jsonData);
                setIsUploading(false);
            };

            reader.onerror = () => {
                setErrors(['Failed to read the file. Please try again.']);
                setIsUploading(false);
            };

            reader.readAsBinaryString(formData.get('file'));
        } catch (error) {
            setErrors(['An error occurred while processing the file.']);
            setIsUploading(false);
            console.error('Error parsing Excel file:', error);
        }
    };

    const handleCancelUpload = () => {
        setFileData(null);
        setItems([]);
        setErrors([]);
        setEditingCell(null);
    };

    const handleCellEdit = (rowIndex, field) => {
        setEditingCell({ rowIndex, field });
        setEditValue(items[rowIndex][field]?.label || '');
    };

    const handleCellSave = () => {
        if (editingCell) {
            const { rowIndex, field } = editingCell;
            const newItems = [...items];
            let error = '';
            let formattedValue = editValue;

            if (field === 'name') {
                error = !editValue.trim() ? 'Name is required' : '';
            } else if (field === 'phone') {
                error = validatePhone(editValue);
            } else if (field === 'email') {
                error = !editValue ? 'Email is missing' : !isValidEmail(editValue) ? 'Invalid email' : '';
            } else if (field === 'role') {
                formattedValue = getRole({ teamId, roomId, role: editValue });
            }

            newItems[rowIndex][field] = {
                label: formattedValue,
                error: error,
            };

            setItems(newItems);
            setEditingCell(null);
            setEditValue('');
        }
    };

    const handleCellCancel = () => {
        setEditingCell(null);
        setEditValue('');
    };

    const applyCountryCodeToAll = () => {
        if (!countryCodePrefix.trim()) {
            toast.error('Please enter a country code prefix first');
            return;
        }

        const newItems = items.map((item) => {
            const currentPhone = item.phone?.label || '';
            // Only update phones that don't already have a country code
            if (currentPhone && !currentPhone.includes('+')) {
                const formattedPhone = formatPhoneWithCountryCode(currentPhone, countryCodePrefix);
                return {
                    ...item,
                    phone: {
                        label: formattedPhone,
                        error: validatePhone(formattedPhone),
                    },
                };
            }
            return item;
        });
        setItems(newItems);
        toast.success(`Applied ${countryCodePrefix} to phones without country codes`);
    };

    const handleSubmit = async () => {
        try {
            if (!teamId && !roomId) {
                return toast.error('Team or Room is required.');
            }
            const data = preparePayload(items || []);
            const response = await Swal.fire({
                title: `Confirm creating ${bulkCategory}`,
                html: `
                    <div>
                    <p style="font-size: 14px;">
                        Total ${bulkCategory} Count: ${(items || []).length}
                    </p>
                    <p style="font-size: 14px; color:green">
                        Valid ${bulkCategory}  Count: ${(data || []).length}
                    </p>
                     <p style="font-size: 14px;color:red;">
                        Invalid ${bulkCategory}  Count: ${(items || []).length - (data || []).length}
                    </p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Confirm',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
            });

            if (!response.isConfirmed) {
                return;
            }
            let res;
            if (roomId) {
                res = await fetchWrapper.post(`chatrooms/${roomId}/invite/bulk`, data);
            } else {
                res = await fetchWrapper.post(
                    `organizations/5f3569c2647fb5000c019e42/teams/${teamId}/invite/bulk`,
                    data
                );
            }

            if (res.error) {
                toast.error(res?.error);
            }
            toast.success(res?.message);
            await showBulkReport(res);
            await callback();
        } catch (error) {
            console.log('ERROR => ', error);
            toast.error(error?.message);
        }
    };

    const renderEditableCell = (item, field, rowIndex) => {
        const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

        if (isEditing) {
            return (
                <div className="d-flex flex-column ">
                    {field === 'role' ? (
                        <select
                            className="form-select form-select-sm "
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            style={{ width: '100%', marginRight: '5px' }}
                        >
                            {(roomId ? ['supervisor', 'doctor', 'family', 'user'] : ['user', 'teamManager']).map(
                                (role) => (
                                    <option key={role} value={role}>
                                        {role}
                                    </option>
                                )
                            )}
                        </select>
                    ) : (
                        <input
                            className="form-control form-control-sm"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            style={{ width: field === 'name' ? '100%' : '100%', marginRight: '5px' }}
                        />
                    )}
                    <div className="d-flex flex-row align-items-center justify-content-center gap-3 p-2">
                        <BootstrapToolTip text="Save">
                            <Button variant="primary" onClick={handleCellSave}>
                                <FaSave size={14} />
                            </Button>
                        </BootstrapToolTip>
                        <BootstrapToolTip text="Cancel">
                            <Button variant="secondary" onClick={handleCellCancel} className="ml-2">
                                <FaTimes size={14} />
                            </Button>
                        </BootstrapToolTip>
                    </div>
                </div>
            );
        }

        return (
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <div>{item[field]?.label}</div>
                    {item[field]?.error && (
                        <p className="text-danger font-bold mb-0" style={{ fontSize: '11px' }}>
                            {item[field]?.error}
                        </p>
                    )}
                </div>
                <BootstrapToolTip text="Edit Cell">
                    <FaEdit
                        size={18}
                        color={'#ea9941'}
                        className="cursor-pointer"
                        onClick={() => handleCellEdit(rowIndex, field)}
                    />
                </BootstrapToolTip>
            </div>
        );
    };

    return (
        <Fragment>
            <div className="ml-2">
                <BootstrapToolTip text={`Bulk Invite To ${roomId ? 'Channel' : 'Team'}`}>
                    <Dropdown.Item onClick={() => setShow(true)} style={{ borderRadius: '4px' }}>
                        <HiUsers />
                    </Dropdown.Item>
                </BootstrapToolTip>
            </div>

            <Modal show={show} onHide={() => setShow(false)} size="xl" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Bulk {bulkCategory} Invite To {roomId ? 'Channel' : 'Team'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!fileData ? (
                        <>
                            <FileUploader
                                accept=".xlsx, .xls"
                                onFileUpload={handleFileUpload}
                                buttonText="Upload Excel File"
                                loadingText="Uploading..."
                                successMessage="File uploaded successfully!"
                                errorMessage="File upload failed. Please try again."
                                fileLimit={5}
                                showTips={true}
                                tips={['Ensure the file is in .xlsx format.']}
                            />

                            {isUploading && (
                                <div className="d-flex justify-content-center">
                                    <VscLoading className="spin" size={30} />
                                </div>
                            )}
                            {errors.length > 0 && (
                                <div className="alert alert-danger mt-3">
                                    <ul>
                                        {errors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    ) : (
                        <div
                            css={css`
                                width: 100%;
                                overflow-x: auto;
                            `}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <div># Note:</div>
                                    <ul>
                                        <li>
                                            <FaInfoCircle color={'orange'} /> Click the edit icon to modify any field
                                        </li>
                                        <li>
                                            <FaInfoCircle color={'orange'} /> If valid role is not provided then,{' '}
                                            <strong>"user"</strong> will be default role.
                                        </li>
                                        <li>
                                            <FaInfoCircle color={'orange'} /> Valid Phone Format (e.g US):{' '}
                                            <strong>+1XXXXXXXXXX</strong>
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <div className="input-group" style={{ width: '300px' }}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Phone Prefix"
                                            value={countryCodePrefix}
                                            onChange={(e) => setCountryCodePrefix(e.target.value)}
                                        />
                                        <Button variant="outline-primary" onClick={applyCountryCodeToAll}>
                                            <span
                                                style={{
                                                    fontSize: '14px',
                                                }}
                                            >
                                                Apply to All
                                            </span>
                                        </Button>
                                    </div>
                                    <div className="text-muted">
                                        <i>
                                            Adds country code prefix to phones without <strong>"+"</strong>
                                        </i>
                                    </div>
                                </div>
                            </div>

                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>S.N.</th>
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td style={{ minWidth: '200px' }}>
                                                {renderEditableCell(item, 'name', index)}
                                            </td>
                                            <td style={{ minWidth: '200px' }}>
                                                {renderEditableCell(item, 'phone', index)}
                                            </td>
                                            <td style={{ minWidth: '200px' }}>
                                                {renderEditableCell(item, 'email', index)}
                                            </td>
                                            <td style={{ minWidth: '150px' }}>
                                                {renderEditableCell(item, 'role', index)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            handleCancelUpload();
                            setShow(false);
                        }}
                    >
                        Close
                    </Button>
                    {fileData && (
                        <Button variant="secondary" onClick={handleCancelUpload}>
                            Upload Another File
                        </Button>
                    )}
                    <Button variant="primary" onClick={handleSubmit} disabled={isUploading || !fileData}>
                        {isUploading ? <Spinner animation="border" size="sm" /> : `Invite Bulk ${bulkCategory} `}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    teamMembers: state.teams.teamMembers,
});

export default connect(mapStateToProps)(BulkModal);
